import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useAppTheme } from 'hooks/useAppTheme';
import React from 'react';

type PerformanceGraphProps = {
  xAxis: {
    min?: number;
    max?: number;
    tickInterval?: number;
    formatter(value: number): string;
  };
  yAxis: {
    min?: number;
    max?: number;
    tickInterval?: number;
    tickAmount?: number;
    labels?: Highcharts.YAxisLabelsOptions;
    // formatter(value: number): string;
  };
  tooltipFormatter: (x: number, y: number) => string;
  data: number[][] | undefined;
  headerControls?: React.ReactElement;
  portfolioGainsPercentage: number;
  isGreaterThanHundredThousand?: boolean;
};

export default function PerformanceGraph({
  xAxis,
  yAxis,
  tooltipFormatter,
  data,
  headerControls,
  portfolioGainsPercentage,
  isGreaterThanHundredThousand,
}: PerformanceGraphProps) {
  const { theme } = useAppTheme();

  const gainLossMetric = portfolioGainsPercentage;
  const gainLoss = gainLossMetric || 0;
  const gainLossColor = gainLoss >= 0 ? theme.success : theme.error;
  const graphColor = portfolioGainsPercentage ? gainLossColor : '#FFFFFF';

  const options: Highcharts.Options = {
    title: {
      text: '',
    },
    chart: {
      backgroundColor: 'transparent',
      spacingTop: 0,
      spacingLeft: 0,
      marginTop: 6,
      marginLeft: 0,
      marginBottom: 30,
      marginRight: isGreaterThanHundredThousand ? 46 : 60,
      style: {
        fontFamily: 'Bozon',
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      title: undefined,
      lineWidth: data ? 0 : 2,
      minorGridLineWidth: 0,
      lineColor: data ? 'transparent' : '#FFFFFF',
      ...xAxis,
      tickLength: 0,
      labels: {
        enabled: true,
        useHTML: true,
        align: 'left',
        formatter() {
          return xAxis.formatter(this.value as number);
        },
        style: {
          fontFamily: 'Bozon',
        },
      },
    },
    yAxis: {
      gridLineWidth: 0,
      opposite: true,
      title: undefined,
      ...yAxis,
      endOnTick: false,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      formatter() {
        return tooltipFormatter(this.x, this.y!);
      },
      padding: 0,
      backgroundColor: 'transparent',
      borderWidth: 0,
      shadow: false,
      borderRadius: 5,
      hideDelay: 2000000,
      useHTML: true,
      style: {
        fontFamily: 'Bozon',
      },
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, `${graphColor}95`],
            [1, `${graphColor}00`],
          ],
        },
        lineColor: graphColor,
        marker: {
          enabled: false,
        },
        lineWidth: 2,
        states: {
          hover: {
            lineWidth: 2,
          },
        },
        threshold: null,
      },
    },
    series: [
      {
        type: 'areaspline',
        name: 'USD to EUR',
        data: data || [],
      },
    ],
  };

  return (
    <div className="h-full w-full rounded-xl py-3 px-5 flex flex-col bg-background">
      <div className="flex items-center mb-2" style={{ marginRight: '56px' }}>
        {headerControls}
      </div>
      {/* https://github.com/highcharts/highcharts/issues/6427#issuecomment-1120524386 */}
      <div style={{ flexGrow: 1, position: 'relative' }}>
        <div style={{ position: 'absolute', height: '100%', width: '100%' }}>
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            highcharts={Highcharts}
            options={options}
          />
        </div>
      </div>
    </div>
  );
}
